import * as ents from '@attain-apps/entities'
import { TypeAsserter } from '@attain-apps/type-guards'
import cytoscape from 'cytoscape'

import type { Feature } from '../feature'
import * as models from '../models'

import * as effects from './effects'
import { getElements } from './elements'
import * as setup from './setup'
import { getStyles } from './styles'

/**
 * Manages {@link cytoscape} instances for the given {@link Feature}
 */
export class Manager {
  /**
   * Lookup of all {@link cytoscape.Core} managed by this {@link Manager}
   */
  private readonly _instances: Partial<
    Record<models.Chart.Id, cytoscape.Core>
  > = {}

  /**
   * Lookup of all navigator instances managed by this {@link Manager}
   */
  private readonly _navigators: Partial<
    Record<models.Chart.Id, setup.NavigatorApi>
  > = {}

  constructor(private readonly _feature: Feature) {
    _feature.registerEffects(
      effects.createCytoscapeInstance(this),
      effects.destroyCytoscapeInstance(this),
      effects.clearCytoscape(this),
      effects.cleanUpCytoscape(this),
      effects.addNodeToCytoscape(this),
      effects.putNodeInCytoscape(this),
      effects.saveEditsToCytoscape(this),
      effects.deleteNodeFromCytoscape(this),
      effects.assignNodeInCytoscape(this),
      effects.undoRedoInCytoscape(this),
      effects.applyApprovedProposalToCytoscape(this),
    )
  }

  /**
   * {@link cytoscape.Core} that corresponds to the given
   * {@link models.Chart.Identifier}
   */
  public getInstance({
    chartId: orgChartId,
  }: models.Chart.Identifier): cytoscape.Core | undefined {
    return this._instances[orgChartId]
  }

  /**
   * Returns the selected {@link models.Node.id} in the {@link cytoscape.Core} for the given
   * {@link models.Chart.Identifier}
   */
  public getSelectedNodeId(
    identifier: models.Chart.Identifier,
  ): ents.core.display.tree.Node.Id | undefined {
    return this.getInstance(identifier)
      ?.nodes('node:selected')
      .map(node =>
        TypeAsserter.validate(
          node.id(),
          ents.core.display.tree.Node.Id.assertIsId,
        ),
      )[0]
  }

  /** Returns whether every visible node in the chart is currently expanded. */
  public isAllNodesExpanded(identifier: models.Chart.Identifier): boolean {
    const cy = this.getInstance(identifier)
    if (!cy) {
      return models.Chart.Entry.get(identifier.chartId) === 'proposed'
    }
    return setup.isAllNodesExpanded(cy)
  }

  /** Toggles the chart between fully expanded and default-depth layouts. */
  public toggleExpandCollapseAll(
    identifier: models.Chart.Identifier,
    options?: setup.toggleExpandCollapseAll.AdditionalParams,
  ): void {
    const cy = this.getInstance(identifier)
    if (!cy) {
      return
    }
    setup.toggleExpandCollapseAll(cy, options)
  }

  /**
   * Sets up the {@link cytoscape.Core} for the given
   * {@link models.Chart.Identifier}
   */
  public create({
    container,
    chartId,
    navigatorContainer,
  }: models.Binding): void {
    const data = this._feature.selectors.getChartData({ chartId }).asSignal()
    const isProposed = models.Chart.Entry.get(chartId) === 'proposed'
    // Destroys the old instance, if there is one
    this.destroy({ chartId })
    // Creates the new instance
    const cy = (this._instances[chartId] = cytoscape({
      autoungrabify: !isProposed,
      container: container(),
      elements: data && getElements(data),
      layout: { name: 'preset' },
      style: getStyles({ chartId }),
    }))

    if (isProposed) {
      setup.setupDragAndDrop(cy, { chartId, feature: this._feature })
    }
    // TODO figure out do we want to edit node on select. If not, remove setupEditOnSelect
    // setup.setupEditOnSelect(cy, { chartId, feature: this._feature })
    setup.setupNodeClasses(cy, { chartId, feature: this._feature })
    setup.setupEdgeHighlight(cy)
    setup.setupExpandCollapse(cy, {
      defaultVisibleDepth: isProposed ? Number.POSITIVE_INFINITY : undefined,
    })

    // Setup navigator (minimap) if container is provided
    if (navigatorContainer) {
      this._navigators[chartId] = setup.setupNavigator(cy, {
        container: navigatorContainer(),
      })
    }
  }

  /**
   * Tears down the {@link cytoscape.Core} for the given
   * {@link models.Chart.Identifier}
   */
  public resetLayout(identifier: models.Chart.Identifier): void {
    const cy = this.getInstance(identifier)
    if (!cy || cy.destroyed() || !cy.container()) {
      return
    }
    cy.resize()
    setup.runLayoutPreservingViewport(cy)
  }

  /**
   * Tears down the {@link cytoscape.Core} for the given
   * {@link models.Chart.Identifier}
   */
  public destroy(identifier: models.Chart.Identifier): void {
    // Destroy navigator first
    const nav = this._navigators[identifier.chartId]
    if (nav) {
      setup.destroyNavigator(nav)
      delete this._navigators[identifier.chartId]
    }
    const cy = this.getInstance(identifier)
    if (!cy) {
      return
    }

    delete this._instances[identifier.chartId]
    cy.destroy()
  }
}
